import { getCurrentEnvironment } from "./api";
import { resolveBatchClass } from "../utils/batchGradingStatus";

// 高中班級批次上傳 API adapter — 走 hanlin-ai-batch-composition-api(與國中共用批次系統)
//
// 訂單帶 hs 欄位 → worker 以高中 prompt 批改、回寫高中批改紀錄
// (檢視紀錄師生同列表)。詳見批次 repo docs/highschool-batch-support.md。
//
// 批次系統自有「班級/作文題目」容器(供訂單分組與進度查詢),
// 由 adapter 依高中真班級/題目 find-or-create 對映,精靈 UI 不需感知。

// getCurrentEnvironment 對 qa-* 網域回 'staging' — 缺這個鍵曾讓 QA 站
// 送單/座號辨識全打到正式站後端(2026-07-13 實證),三個環境都要有對映
const BATCH_API_ENDPOINTS = {
  development: "https://qa-hanlin-ai-batch-composition-api.hle.com.tw",
  staging: "https://qa-hanlin-ai-batch-composition-api.hle.com.tw",
  production: "https://hanlin-ai-batch-composition-api.hle.com.tw",
} as const;

const AUTH_KEY = "halnin-ai-batch"; // 後端如此拼字(沿用國中,勿「修正」)

const batchApiBase = () =>
  BATCH_API_ENDPOINTS[
    getCurrentEnvironment() as keyof typeof BATCH_API_ENDPOINTS
    // 未知環境寧可打 QA、不可誤寫正式庫
  ] ?? BATCH_API_ENDPOINTS.development;

/** 批次 API 非 2xx 的錯誤：帶 path 與 status，讓呼叫端能分辨是哪個端點、什麼狀況（kanban 1000892） */
export class BatchApiError extends Error {
  readonly path: string;
  readonly status: number;
  readonly body: string;
  constructor(path: string, status: number, body: string) {
    super(`批次 API ${path} 失敗(${status}):${body.slice(0, 200)}`);
    this.name = "BatchApiError";
    this.path = path;
    this.status = status;
    this.body = body;
  }
}

const batchFetch = async (path: string, init?: RequestInit) => {
  const headers: Record<string, string> = {
    auth: AUTH_KEY,
    ...((init?.headers as Record<string, string>) || {}),
  };
  if (init?.body && !(init.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`${batchApiBase()}${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new BatchApiError(path, res.status, text);
  }
  // 204 No Content(如 PUT /batch-results/{id}/status):空 body 硬 json() 會 throw
  if (res.status === 204) return null;
  return res.json();
};

export interface BatchStudent {
  seatNo: string; // 顯示用座號(如 "01")
  seatNoInt: number;
  userName: string; // 學生姓名,可空字串
  files: File[]; // 該生的作文圖片(依頁序)
  originFiles: File[]; // 校正後整頁圖(暫未使用,保留欄位)
}

export interface BatchSubmitContext {
  bookId: string;
  chapterId: string;
  chapterName: string; // 批次系統 composition 標題
  classCode: string; // 高中真班級邀請碼
  className: string;
  teacherId: string; // 批次系統 creatorID
}

export type BatchItemStatus =
  | "pending"
  | "uploading"
  | "submitted"
  | "failed";

export interface BatchItemProgress {
  seatNo: string;
  userName: string;
  status: BatchItemStatus;
  error?: string;
}

export interface BatchSubmitResult {
  batchClassId: string; // 供 fetchBatchResultsByClass 輪詢進度
  orderId: string;
  items: BatchItemProgress[];
}

// ── 批次系統容器 find-or-create ──────────────────────────────────────────

// 檢視紀錄頁也用:以班級碼對出 batch classID 後拉 batch-results 顯示
// 批改中/失敗(kanban 106957)。解析邏輯在 utils/batchGradingStatus 的
// resolveBatchClass,與下方 ensureBatchClass 同一套。
//
// bookID 一律要帶(kanban 106957):批次班級以 classCode + creatorID + bookID 為
// 唯一鍵,不帶就會撈到別本書的同名班,兩本書的批改紀錄併進同一個批次容器。
// 後端的 bookID 是選填(國中沒有書籍維度),但高中這邊沒有不帶的情境。
// classCode:高中班級邀請碼;班級碼功能上線前建立的舊容器為 null。
export interface BatchClass {
  classID: string;
  className: string;
  classCode?: string | null;
}

export const fetchBatchClassesByCreator = (
  creatorID: string,
  bookID: string
): Promise<BatchClass[]> =>
  batchFetch(
    `/classes/creator/${encodeURIComponent(creatorID)}?bookID=${encodeURIComponent(bookID)}`
  );

// 容器一律以班級碼對應(kanban 106957):老師刪班重建同名班會拿到新碼,
// 用班名找會讓新班接手舊容器 — 舊班在途批改混進新班、新班拿不到紀錄。
//   (a) 碼相符 → 直接用;容器班名與當下不同時先 PUT 同步班名(kanban 1000490):
//       老師在派出作文改班名只更新主系統,容器仍記舊名,送單帶新名會被後端
//       Class name mismatch 擋下。帶碼容器改名後端不查重,失敗照常拋錯、不沿用舊名送單
//   (b) 同名且尚未寫入碼的舊容器 → PUT 認領(補寫 classCode);撞碼後端回 400,照常拋錯
//   (c) 都沒有 → POST 新建,帶 classCode 讓後端以(老師、書、班級碼)唯一,允許同名不同碼
export const ensureBatchClass = async (
  className: string,
  creatorID: string,
  bookID: string,
  classCode: string
): Promise<BatchClass> => {
  const list = await fetchBatchClassesByCreator(creatorID, bookID);
  const resolved = resolveBatchClass(list, className, classCode);
  if (resolved.kind === "match") {
    const found = list.find((c) => c.classID === resolved.classID)!;
    if (found.className === className) return found;
    return batchFetch(`/classes/${encodeURIComponent(found.classID)}`, {
      method: "PUT",
      body: JSON.stringify({ className }),
    });
  }
  if (resolved.kind === "adopt") {
    return batchFetch(`/classes/${encodeURIComponent(resolved.classID)}`, {
      method: "PUT",
      body: JSON.stringify({ classCode }),
    });
  }
  return batchFetch("/classes", {
    method: "POST",
    body: JSON.stringify({ className, creatorID, bookID, classCode }),
  });
};

const ensureComposition = async (params: {
  compositionTitle: string;
  classID: string;
  className: string;
  creatorID: string;
}): Promise<{ compositionID: string; compositionTitle: string }> => {
  const list = await batchFetch(
    `/compositions/class/${encodeURIComponent(params.classID)}`
  );
  const existing = Array.isArray(list)
    ? list.find(
        (c: { compositionTitle: string }) =>
          c.compositionTitle === params.compositionTitle
      )
    : null;
  if (existing) return existing;
  return batchFetch("/compositions", {
    method: "POST",
    body: JSON.stringify(params),
  });
};

const uploadFile = async (
  file: File,
  params: {
    classID: string;
    compositionID: string;
    seatNo: string;
    userName: string;
    uploadNo: number;
  }
): Promise<{ url: string; key: string }> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("classID", params.classID);
  formData.append("compositionID", params.compositionID);
  formData.append("seatNo", params.seatNo);
  formData.append("userName", params.userName);
  formData.append("uploadNo", String(params.uploadNo));
  return batchFetch("/uploads", { method: "POST", body: formData });
};

// ── 主流程:上傳整班圖片(節流)→ 建一張批次訂單 ─────────────────────────

export async function submitBatch(
  ctx: BatchSubmitContext,
  students: BatchStudent[],
  options?: {
    concurrency?: number;
    onProgress?: (progress: BatchItemProgress[]) => void;
  }
): Promise<BatchSubmitResult> {
  const concurrency = options?.concurrency ?? 3;
  const progress: BatchItemProgress[] = students.map((s) => ({
    seatNo: s.seatNo,
    userName: s.userName,
    status: "pending",
  }));
  const notify = () => options?.onProgress?.([...progress]);

  const batchClass = await ensureBatchClass(
    ctx.className,
    ctx.teacherId,
    ctx.bookId,
    ctx.classCode
  );
  const composition = await ensureComposition({
    compositionTitle: ctx.chapterName,
    classID: batchClass.classID,
    className: ctx.className,
    creatorID: ctx.teacherId,
  });

  // 每生圖片上傳(節流:同時最多 N 生)
  const uploadedUrls: string[][] = students.map(() => []);
  let next = 0;
  const worker = async () => {
    while (next < students.length) {
      const index = next++;
      const student = students[index];
      progress[index].status = "uploading";
      notify();
      try {
        for (let i = 0; i < student.files.length; i++) {
          const { url } = await uploadFile(student.files[i], {
            classID: batchClass.classID,
            compositionID: composition.compositionID,
            seatNo: student.seatNo,
            userName: student.userName,
            uploadNo: i + 1,
          });
          uploadedUrls[index].push(url);
        }
      } catch (error) {
        progress[index].status = "failed";
        progress[index].error =
          error instanceof Error ? error.message : String(error);
        notify();
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, students.length) }, worker)
  );

  const okIndexes = students
    .map((_, i) => i)
    .filter((i) => progress[i].status !== "failed");
  if (okIndexes.length === 0) {
    throw new Error("所有學生的圖片上傳都失敗,未建立批改訂單");
  }

  const order = await batchFetch("/batch-orders", {
    method: "POST",
    body: JSON.stringify({
      classID: batchClass.classID,
      className: ctx.className,
      compositionID: composition.compositionID,
      compositionTitle: ctx.chapterName,
      uploads: okIndexes.map((i) => ({
        seatNo: students[i].seatNo,
        userName: students[i].userName,
        imageUrls: uploadedUrls[i],
      })),
      creatorID: ctx.teacherId,
      correctionMode: "fast",
      hs: {
        service: "NewAICorrecting",
        bookId: ctx.bookId,
        chapterId: ctx.chapterId,
        classCode: ctx.classCode,
        className: ctx.className,
      },
    }),
  });

  okIndexes.forEach((i) => {
    progress[i].status = "submitted";
  });
  notify();

  return {
    batchClassId: batchClass.classID,
    orderId: order.orderID,
    items: progress,
  };
}

// 進度輪詢:status open/running/finish/failed/prohibited(每筆一位學生)
export const fetchBatchResultsByClass = (classID: string) =>
  batchFetch(`/batch-results/class/${encodeURIComponent(classID)}`);

// 重新批改:把 failed 的批改結果改回待處理,worker 會重跑
// (與國中 batchApiUtils reopenResult 同一支 PUT,後端回 204)
export const reopenBatchResult = (resultID: string): Promise<void> =>
  batchFetch(`/batch-results/${encodeURIComponent(resultID)}/status`, {
    method: "PUT",
  });

// 移除批改結果:失敗/無法批改列的清除途徑,沒這個會永遠掛在檢視紀錄上
// (與國中 batchApiUtils deleteResult 同一支 DELETE,後端回 204)
export const deleteBatchResult = (resultID: string): Promise<void> =>
  batchFetch(`/batch-results/${encodeURIComponent(resultID)}`, {
    method: "DELETE",
  });

// 每日額度國中、高中各自一份(kanban 1000892),高中端查自己那份
export const fetchBatchRemainingQuota = (creatorID: string) =>
  batchFetch(`/batch-orders/remaining/${encodeURIComponent(creatorID)}?school=hs`);
